// Dependencies
import styled, { css } from 'styled-components';

// Components
import { Typography } from '../../../display/Typography';
import { ArrowBottom } from '../../../../assets/icons/ArrowBottom';

// Styles
import { hoverCapable } from '../../../../styles/hover';

// Types
import { InputSizeTypes, TypeDropDown } from './types';

type ContainerProps = {
  typeDropDown: TypeDropDown;
  labelText: boolean;
  inputSize: InputSizeTypes;
};

type HeaderProps = {
  typeDropDown: TypeDropDown;
  isActive: boolean;
  borderError?: boolean;
  isDisabled?: boolean;
};

type ListItemProps = {
  isSelected: boolean;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs};
  cursor: pointer;
  width: 100%;
  ${({ inputSize }) =>
    ({
      XXL: css`
        width: 38rem;
      `,
      XL: css`
        width: 35rem;
      `,
      L: css`
        width: 27rem;
      `,
      M: css`
        width: 22rem;
      `,
      S: css`
        width: 17rem;
      `,
      XS: css`
        width: 10rem;
      `,
    })[inputSize]};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    ${({ inputSize }) =>
      ({
        XXL: css`
          width: 100%;
          max-width: 38rem;
        `,
        XL: css`
          width: 100%;
          max-width: 35rem;
        `,
        L: css`
          width: 100%;
          max-width: 27rem;
        `,
        M: css`
          width: 100%;
          max-width: 22rem;
        `,
        S: css`
          width: 100%;
          max-width: 17rem;
        `,
        XS: css`
          width: 100%;
          max-width: 10rem;
        `,
      })[inputSize]};
  }
`;

export const Label = styled(Typography)`
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.S};
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const DropDownContainer = styled.div`
  position: relative;
`;

export const DropDownHeader = styled.div<HeaderProps>`
  display: flex;
  justify-content: space-between;
  /* Mismo patrón que DropDownInput.DropDownHeader: align-items center +
     padding solo lateral. El SelectedContent interno mantiene su propio
     wrap layout para chips (align-items: center, align-content: flex-start). */
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: 0 0.8rem 0 1rem;
  min-height: 4rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.black};
  /* Header con altura auto: crece libremente con los chips (patrón
     react-select). SIN max-height ni scroll interno — evita el bug de
     align-content:center que renderizaba los chips overflow encima del
     label cuando el contenido excedía el max-height. */

  ${(props) =>
    props.isActive &&
    css`
      outline: 0.08rem solid ${({ theme }) => theme.colors.black};
    `}

  ${(props) =>
    props.typeDropDown === 'Table' &&
    css`
      border: none;
      outline: none;
      background: initial;
    `};

  ${(props) =>
    props.isDisabled &&
    css`
      background: ${({ theme }) => theme.colors.greyLight};
      border: none;
      cursor: not-allowed;
    `};

  ${(props) =>
    props.borderError &&
    css`
      border: 0.1rem solid ${({ theme }) => theme.colors.lightCoralRed};
    `};

  &:focus {
    outline: 0.08rem solid ${({ theme }) => theme.colors.black};
  }
`;

export const SelectedContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
  /* Padding vertical aporta breathing room a chips cuando wrap (sin esto,
     chips de líneas superiores tocan el top del header). Solo aplica cuando
     hay >=1 línea de chips wrapeada; con 1 placeholder no hace diferencia
     porque align-items:center centra. */
  padding: 0.3rem 0;
`;

export const Placeholder = styled(Typography)`
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ theme }) => theme.colors.greySoftLight};
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;
  background-color: ${({ theme }) => theme.colors.backgroundLila};
  border-radius: 0.4rem;
  font-size: ${({ theme }) => theme.fontSize.XS};
  color: ${({ theme }) => theme.colors.black};
  flex-shrink: 0;
  max-width: 100%;
  min-width: 0;
  /* Labels largos: se truncan con ellipsis vía TagLabel + tooltip nativo con
     el atributo title (ver index.tsx) SOLO cuando exceden el ancho del
     container (SelectedContent), no a partir de un umbral fijo. La × siempre
     visible (flex-shrink:0 en TagRemove). El label completo se ve en la
     lista del dropdown. */
`;

export const TagLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const TagRemove = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.black};

  ${hoverCapable} {
    &:hover {
      color: ${({ theme }) => theme.colors.garnet};
    }
  }

  &:focus-visible {
    outline: 0.1rem solid ${({ theme }) => theme.colors.black};
    outline-offset: 0.1rem;
    border-radius: 0.2rem;
  }
`;

export const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.4rem;
  margin: 0;
  flex-shrink: 0;
  /* La flecha hereda el align-items: center del DropDownHeader y queda
     centrada verticalmente — mismo patrón que DropDownInput/SearchableDropdown.
     Cuando hay muchos chips wrapeando, el header crece y la flecha se
     centra con el bloque de chips (aceptable). */
`;

export const ArrowDown = styled(ArrowBottom)`
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
  vertical-align: middle;
  min-width: 1.3rem;
  min-height: 0.9rem;
  flex-shrink: 0;
`;

// List styles
export const DropDownListContainer = styled.div`
  z-index: 999;
  position: absolute;
  width: 100%;
  top: 100%;
  left: 0;
`;

export const List = styled.ul`
  position: absolute;
  border-radius: 0.5rem;
  top: ${({ theme }) => theme.spacing.xxxs};
  color: ${({ theme }) => theme.colors.greyVeryDark};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  min-width: 100%;
  width: auto;
  /* Tope de ancho: sin él, width auto crece hasta la etiqueta más larga y la
     lista se sale de la pantalla. No se notaba porque las opciones de Estado
     o Equipo son cortas («Abierta», «TRAM 1»); con las de Materiales
     («M00001 - Aglomerat en fred en pots de 25 Kg») la lista ocupaba el ancho
     entero de la página. Acotada al disparador, el text-overflow que
     OptionLabel ya traía por fin entra en juego. */
  max-width: 100%;
  max-height: 25rem;
  overflow: auto;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    max-height: 18rem;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-height: 17rem;
  }
`;

export const ListItem = styled.li<ListItemProps>`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  list-style: none;
  font-size: ${({ theme }) => theme.fontSize.S};
  padding: 1rem;
  cursor: pointer;

  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${({ theme }) => theme.colors.backgroundLila};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    `};

  /* Solo con puntero fino: en táctil el :hover se quedaría pegado tras el toque
     y sería indistinguible de una opción marcada (ver styles/hover.ts). Aquí
     duele el doble, porque la lista NO se cierra al elegir. */
  ${hoverCapable} {
    &:hover {
      background-color: ${({ theme }) => theme.colors.backgroundLila};
    }
  }

  /* :focus-visible en vez de :focus — al tocar una opción el <li tabIndex=0>
     se quedaba enfocado con la lista abierta, pintado como si estuviera
     marcado. El resaltado se reserva a la navegación por teclado. */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.backgroundLila};
    outline: none;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 1.6rem;
  height: 1.6rem;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.garnet};
`;

export const OptionLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* ListItem es flex y un hijo flex no baja de su contenido con el
     min-width auto por defecto: sin esto la etiqueta no encoge y desborda
     la lista en vez de recortarse con puntos suspensivos. */
  flex: 1;
  min-width: 0;
`;

// Searchable: input sticky DENTRO del listbox (Patrón A). El header queda
// limpio con chips + placeholder; cuando el dropdown abre, la primera fila
// es esta barra de búsqueda separada por un border-bottom del listado.
// Más legible que ponerlo en el header con muchos chips apilados.
export const SearchInputBox = styled.div`
  padding: 0.6rem 0.8rem;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
  background: ${({ theme }) => theme.colors.white};
  position: sticky;
  top: 0;
  z-index: 1;
`;

// Input transparente — el contenedor SearchInputBox aporta el padding y el
// separador inferior. Mismo cursor pattern que SearchableDropdown.SearchInput.
export const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.S};
  font-family: 'Poppins', sans-serif;
  color: ${({ theme }) => theme.colors.greyVeryDark};
  cursor: pointer;
  padding: 0;
  /* Quitar la × nativa de type="search" en Chrome (innecesaria — Escape
     limpia y clickAway cierra). */
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.greySoftLight};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
  }

  &:focus {
    cursor: text;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.greyDark};
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const NoResultsText = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.XS};
  color: ${({ theme }) => theme.colors.greyDark};
`;
